import React, { useState, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    FlatList,
    TouchableOpacity,
    StyleSheet,
    ActivityIndicator,
    SafeAreaView,
    Platform,
    Alert,
    Modal
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import TopGreenHeader from '../Common/TopGreenHeader';
import { getAllAddressApi } from '../../apiCalls/LoginApi';
import API_PATHS from '../../apiCalls/ApiPath';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { toast } from 'react-toastify';

export const AddressList = () => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const navigation = useNavigation();

    useFocusEffect(
        useCallback(() => {
            fetchAddresses();
        }, [])
    );

    const fetchAddresses = async () => {
        try {
            setLoading(true);
            const data = await getAllAddressApi();

            // Assuming the API returns an array in data or data.Data or data.addresses
            // We ensure addressList is always an array
            let addressList = [];
            if (Array.isArray(data)) {
                addressList = data;
            } else if (data && data.Data && Array.isArray(data.Data)) {
                addressList = data.Data;
            } else if (data && data.addresses && Array.isArray(data.addresses)) {
                addressList = data.addresses;
            } else if (data && typeof data === 'object') {
                // Sometime API might wrap it in some other key, we try to extract
                const possibleArrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
                if (possibleArrayKey) {
                    addressList = data[possibleArrayKey];
                }
            }

            setAddresses(addressList);
        } catch (error) {
            console.error('Error fetching addresses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        setAddressToDelete(id);
        setModalVisible(true);
    };

    const handleConfirmDelete = () => {
        setModalVisible(false);
        if (addressToDelete) {
            confirmDelete(addressToDelete);
        }
    };

    const confirmDelete = async (id) => {
        try {
            setLoading(true);
            const token = await AsyncStorage.getItem('token');
            const response = await fetch(`${API_PATHS.BASE_URL}v1/parent/delete-address`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    addressId: id.toString()
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.Success === 1 || !data.hasOwnProperty('Success') || data.message) {
                    // Refetch the addresses upon success
                    toast.success("Address deleted successfully");
                    await fetchAddresses();
                } else {
                    toast.error(data.Message || "Failed to delete address");
                    setLoading(false);
                }
            } else {
                toast.error("Failed to delete address from server");
                setLoading(false);
            }
        } catch (error) {
            console.error('Error deleting address:', error);
            Alert.alert("Error", "An error occurred while deleting the address");
            setLoading(false);
        }
    };

    const handleAddAddress = () => {
        // Does nothing for now as per instructions
        navigation.navigate('AddAddress');
    };

    const renderItem = ({ item }) => (
        <View style={styles.itemContainer}>
            <View style={styles.addressInfo}>
                <Text style={styles.tagText}>{item.tag || 'Address'}</Text>
                <Text style={styles.addressText}>
                    {item.houseno ? `${item.houseno}, ` : ''}
                    {item.area ? `${item.area}, ` : ''}
                    {item.city ? `${item.city} ` : ''}
                    {item.zipcode ? `- ${item.zipcode}` : ''}
                </Text>
            </View>
            <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => handleDelete(item.id || item._id)}
            >
                <Text style={styles.deleteButtonText}>Delete</Text>
            </TouchableOpacity>
        </View>
    );

    return (
        <SafeAreaView style={styles.container}>
            <TopGreenHeader
                Heading="Addresses"
                navigation={navigation}
                isBackIcon={true}
                isNotification={false}
            />
            {loading ? (
                <View style={styles.loaderContainer}>
                    <ActivityIndicator size="large" color="#A8CF45" />
                </View>
            ) : (
                <FlatList
                    data={addresses}
                    keyExtractor={(item, index) => item.id?.toString() || item._id?.toString() || index.toString()}
                    renderItem={renderItem}
                    ItemSeparatorComponent={() => <View style={styles.separator} />}
                    contentContainerStyle={styles.listContent}
                    ListEmptyComponent={<Text style={styles.emptyText}>No addresses found.</Text>}
                />
            )}

            <View style={styles.bottomContainer}>
                <TouchableOpacity style={styles.addButton} onPress={handleAddAddress}>
                    <Text style={styles.addButtonText}>Add Address</Text>
                </TouchableOpacity>
            </View>

            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={styles.modalContainer}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalText}>Are you sure you want to delete this address?</Text>
                        <View style={styles.modalButtons}>
                            <TouchableOpacity style={styles.button} onPress={handleConfirmDelete}>
                                <Text style={styles.buttonText}>Yes</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.button} onPress={() => setModalVisible(false)}>
                                <Text style={styles.buttonText}>No</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fff',
    },
    loaderContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    listContent: {
        padding: 15,
        paddingBottom: 100, // accommodate for the bottom button container
    },
    itemContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 15,
    },
    addressInfo: {
        flex: 1,
        paddingRight: 15,
    },
    tagText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
        marginBottom: 5,
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        textTransform: 'capitalize',
    },
    addressText: {
        fontSize: 14,
        color: '#666',
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        lineHeight: 20,
    },
    deleteButton: {
        backgroundColor: '#ff4d4f',
        paddingHorizontal: 15,
        paddingVertical: 8,
        borderRadius: 5,
        justifyContent: 'center',
        alignItems: 'center',
    },
    deleteButtonText: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 14,
    },
    separator: {
        height: 1,
        backgroundColor: '#eee',
    },
    bottomContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        padding: 15,
        borderTopWidth: 1,
        borderColor: '#eee',
    },
    addButton: {
        backgroundColor: '#A8CF45',
        padding: 15,
        borderRadius: 10,
        alignItems: 'center',
    },
    addButtonText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: 'bold',
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    },
    emptyText: {
        textAlign: 'center',
        color: '#999',
        marginTop: 50,
        fontSize: 16,
    },
    modalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    modalContent: {
        width: 300,
        padding: 20,
        backgroundColor: '#fff',
        borderRadius: 10,
        alignItems: 'center',
    },
    modalText: {
        fontSize: 18,
        marginBottom: 20,
        color: "#505766",
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        fontStyle: 'normal',
        textAlign: 'center',
    },
    modalButtons: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
    button: {
        flex: 1,
        padding: 10,
        marginHorizontal: 10,
        backgroundColor: '#A6CE39',
        alignItems: 'center',
        borderRadius: 5,
    },
    buttonText: {
        color: '#fff',
        fontSize: 16,
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        fontStyle: 'normal',
        fontWeight: 'bold',
    }
});

export default AddressList;
